<template>
  <rest-page :fetch-endpoint="fetchEndpoint()">
    <div class="scoring-form-ct">
      <div class="block">
        <div class="header">
          <div>
            {{ $t('Gamification_ScoringForm_PredefinedScoringDetails') }}
          </div>
          <div class="description">
            <span>
              {{
                $t(
                  'Gamification_ScoringForm_PredefinedScoringDetailsDescription'
                )
              }}
            </span>
          </div>
        </div>
        <div class="content">
          <div class="section">
            <div>
              <span>{{ $t('Gamification_ScoringForm_ResolutionMethod') }}</span
              ><br />
              <span class="description">{{
                $t('Gamification_ScoringForm_ResolutionMethodDescription')
              }}</span>
            </div>
            <div class="value">{{ dumpModel.resolution_method }}</div>
          </div>
        </div>
      </div>

      <rest-form
        v-if="model"
        ref="scoring-form"
        :model="model"
        :save-endpoint="saveEndpoint"
        :after-save="afterSave"
        :rules="rules"
        :disabled="isReadOnly"
        @on-error="isLoading = false"
      >
        <form-section
          :title="$t('Gamification_ScoringForm_Leaderboard')"
          :description="$t('Gamification_ScoringForm_LeaderboardDescription')"
          is-required
        >
          <rest-form-item
            prop="leaderboard"
            class="default-width"
            required
            :label="$t('Gamification_ScoringForm_EnableLeaderboard')"
          >
            <el-switch
              v-model="model.leaderboard"
              @change="onLeaderboardChange"
              :active-value="true"
              :inactive-value="false"
              active-color="#1d87c9"
            />
          </rest-form-item>
        </form-section>
        <form-section
          :title="$t('Gamification_ScoringForm_PointsLogic')"
          :description="$t('Gamification_ScoringForm_PointsLogicDescription')"
          is-required
        >
          <rest-form-item prop="points_logic" class="default-width" required>
            <el-select
              v-model="model.points_logic"
              filterable
              clearable
              data-qa="points-logic-select"
            >
              <el-option
                v-for="option in pointsLogicOptions"
                :key="option.value"
                :value="option.value"
                :label="option.label"
              />
            </el-select>
          </rest-form-item>
        </form-section>

        <form-section
          v-if="model.leaderboard"
          :title="$t('Gamification_ScoringForm_TiebreakerQuestion')"
          :description="
            $t('Gamification_ScoringForm_TiebreakerQuestionDescription')
          "
          is-required
        >
          <rest-form-item
            prop="tiebreaker_question"
            :label="$t('Gamification_ScoringForm_TiebreakerQuestion')"
            class="default-width"
          >
            <el-input
              v-model="model.tiebreaker_question"
              required
              data-qa="tiebreaker-question-input"
            ></el-input>
          </rest-form-item>

          <rest-form-item
            prop="tiebreaker_correct_answer"
            :label="$t('Gamification_ScoringForm_TiebreakerAnswer')"
            class="default-width"
          >
            <el-input-number
              style="width: 256px"
              :min="1"
              :max="10000"
              :step="1"
              v-model="model.tiebreaker_correct_answer"
              controls-position="right"
              :disabled="isCreationMode || isReadOnly"
              data-qa="tiebreaker-answer-input"
            />
          </rest-form-item>
        </form-section>
      </rest-form>

      <div v-if="isCreationMode" class="footer">
        <div class="footer-ct">
          <el-button
            type="primary"
            :disabled="isSubmitDisabled"
            @click="saveScoring"
            data-qa="next-step-btn"
          >
            {{ $t('Gamification_ScoringForm_SaveButton') }}
            <i class="el-icon-arrow-right el-icon-right"></i>
          </el-button>
        </div>
      </div>
    </div>
  </rest-page>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import RestForm from '@/common/components/rest/RestForm.vue'
import { checkIsButtonDisabled } from '@/modules/gamification/helpers'
import { GameCreationSubSteps } from '@/modules/gamification/enum/GameCreationStepsEnum'
import RestFormItem from '@/common/components/rest/RestFormItem.vue'
import FormSection from '@/common/components/form/FormSection.vue'
import _ from 'lodash'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'

export default {
  name: 'GameScoring',
  components: { RestPage, FormSection, RestFormItem, RestForm },
  data() {
    return {
      isLoading: false,
      model: {
        leaderboard: false,
        points_logic: undefined,
        tiebreaker_question: undefined,
        tiebreaker_correct_answer: undefined
      },
      initialStateModel: undefined,
      shouldGoToNextStep: false
    }
  },
  props: {
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isCreationMode: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    isCompleted: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    }
  },
  computed: {
    isSubmitDisabled() {
      const required = this.model.leaderboard
        ? ['points_logic', 'tiebreaker_question']
        : ['points_logic']

      return checkIsButtonDisabled(required, this.model) || this.isLoading
    },
    hasChanges() {
      return !_.isEqual(this.model, this.initialStateModel)
    },
    rules() {
      const rules = {
        points_logic: {
          required: true,
          message: this.$t('Gamification_ScoringForm_PointsLogicRequired'),
          trigger: 'change'
        }
      }
      if (this.model.leaderboard) {
        rules.tiebreaker_question = {
          required: true,
          message: this.$t(
            'Gamification_ScoringForm_TiebreakerQuestionRequired'
          ),
          trigger: 'change'
        }
      }
      return rules
    },
    pointsLogicOptions() {
      return [
        {
          value: 'basic',
          label: this.$t('Gamification_ScoringForm_PointsLogic_Standard')
        },
        {
          value: 'advancement_multiplier',
          label: this.$t(
            'Gamification_ScoringForm_PointsLogic_AdvancementMultiplier'
          )
        }
        // @todo: Temporarily hidden — feature not yet available from the gamification backend
        // {
        //   value: 'odds_multiplier',
        //   label: this.$t('Gamification_ScoringForm_PointsLogic_OddsMultiplier')
        // }
      ]
    },
    dumpModel() {
      return {
        resolution_method: 'Point accumulation'
      }
    },
    saveEndpoint() {
      return projectEndpoint({
        method: 'put',
        url: `gamify/${this.$route.params.id}/store-scoring/${this.gameCampaignId}`
      })
    }
  },
  mounted() {
    if (!this.isCompleted) {
      if (this.model && this.model.leaderboard === false) {
        this.model.points_logic = this.model.points_logic || 'basic'
      }
      this.initialStateModel = _.cloneDeep(this.model)
    }
  },
  watch: {
    isSubmitDisabled: {
      handler(newVal) {
        this.$emit('check', newVal)
      },
      immediate: true
    },
    hasChanges: {
      handler(newVal) {
        this.$emit('checkChanges', newVal)
      },
      immediate: true
    },
    saveFlag: {
      async handler() {
        await this.saveScoring(false)
      }
    }
  },
  methods: {
    onLeaderboardChange(val) {
      if (!val) {
        this.model.tiebreaker_question = undefined
        this.model.tiebreaker_correct_answer = undefined
      }
    },
    saveScoring(shouldGoToNextStep = true) {
      if (!this.model.leaderboard) {
        this.model.tiebreaker_question = undefined
        this.model.tiebreaker_correct_answer = undefined
      }
      this.isLoading = true
      this.$refs['scoring-form'].save()
      this.shouldGoToNextStep = shouldGoToNextStep
    },
    afterSave(data, finishLoading) {
      this.initialStateModel = _.cloneDeep(this.model)
      finishLoading()
      this.isLoading = false

      if (this.shouldGoToNextStep) {
        this.$emit('goToNextStep', GameCreationSubSteps.SCORING)
      } else if (this.isCreationMode) {
        EventBus.$emit('updateStep', GameCreationSubSteps.SCORING)
        this.$nextTick(() => {
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        })
      }
    },
    fetchEndpoint() {
      const isGamePage = [
        'GameEditCampaignPage',
        'GameViewCampaignPage'
      ].includes(this.$route.name)

      if (!isGamePage && !this.isCompleted) {
        return null
      }

      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-scoring-configuration/${this.gameCampaignId}`,
        handler: data => {
          this.model = data['data']
          this.initialStateModel = _.cloneDeep(this.model)
        }
      })
    }
  }
}
</script>

<style scoped>
.scoring-form-ct {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.block {
  font-size: 14px;
  font-weight: 600;
  color: #4b515f;
  border: 1px solid #dbdee7;
  background-color: #fcfcfd;

  .description {
    font-size: 12px;
    font-weight: 400;
    color: #5c6374;
  }

  .value {
    font-weight: 400;
  }

  .header {
    padding: 10px 24px;
  }

  .content {
    border-top: 1px solid #dbdee7;
    padding: 10px 24px;
    gap: 25px;
    display: flex;
    flex-direction: column;
  }

  .section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 80%;
    gap: 15px;
  }
}

.default-width {
  width: 596px;
}

.footer {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  background: #ffffff;
  z-index: 1;
  border-top: 1px solid #e5e5e5;
}
.footer-ct {
  margin-right: 20px;
  margin-bottom: 12px;
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/game-rules/GameScoringForm.vue